(() => {
  if (window.__zingInfiniteScrollLoaded) return;
  window.__zingInfiniteScrollLoaded = true;

  const states = new Set();
  let mutationTimer = null;

  const cleanupDetached = () => {
    states.forEach((state) => {
      if (!document.contains(state.pagination)) {
        state.observer?.disconnect();
        states.delete(state);
      }
    });
  };

  const failGracefully = (state) => {
    state.loading = false;
    state.observer?.disconnect();
    state.pagination.classList.remove('zing-infinite-ready');
    if (state.sentinel) {
      state.sentinel.classList.remove('zing-infinite-ready');
    }
  };

  const loadNext = async (state) => {
    if (state.loading || !state.nextUrl || state.loadedUrls.has(state.nextUrl)) return;
    state.loading = true;
    state.loadedUrls.add(state.nextUrl);
    if (state.clickMode) {
      state.sentinel.classList.add('zing-infinite-loading');
      state.button.disabled = true;
    }

    try {
      const response = await fetch(state.nextUrl, {
        credentials: 'same-origin',
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      });
      if (!response.ok) throw new Error(`Infinite scroll request failed: ${response.status}`);

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const nextGrid = doc.querySelector('#facets-results > ul');
      const nextPagination = doc.querySelector('[data-infinite-pagination]');
      const nextSentinel = doc.querySelector('[data-infinite-sentinel]');

      if (!nextGrid) throw new Error('Infinite scroll product grid not found');

      const items = Array.from(nextGrid.children);
      if (!items.length) throw new Error('Infinite scroll returned no products');

      const fragment = document.createDocumentFragment();
      items.forEach((item) => fragment.appendChild(item));
      state.grid.appendChild(fragment);

      const nextUrl = nextSentinel?.dataset.nextUrl || null;
      if (nextUrl && !state.loadedUrls.has(nextUrl)) {
        state.nextUrl = nextUrl;
        state.sentinel.dataset.nextUrl = nextUrl;
        // Keep the translated initial status message for this visitor.
      } else {
        state.nextUrl = null;
        state.observer?.disconnect();
        state.sentinel.classList.add('zing-infinite-done');
        state.sentinel.removeAttribute('data-next-url');
      }

      if (!nextPagination && !nextSentinel) {
        state.observer?.disconnect();
      }
    } catch (error) {
      console.warn('[ZingCandy] Infinite scroll fallback enabled.', error);
      failGracefully(state);
    } finally {
      state.loading = false;
      if (state.clickMode) {
        state.sentinel.classList.remove('zing-infinite-loading');
        state.button.disabled = false;
      }
    }
  };

  const initPagination = (pagination) => {
    if (pagination.dataset.infiniteInitialized === 'true') return;

    const root = pagination.closest('#facets-filterable') || document;
    const grid = root.querySelector('#facets-results > ul') || document.querySelector('#facets-results > ul');
    const sentinel = root.querySelector('[data-infinite-sentinel]') || pagination.parentElement?.querySelector('[data-infinite-sentinel]');

    const clickMode = sentinel?.dataset.loadMode === 'click';
    const button = clickMode ? sentinel.querySelector('[data-infinite-load-more]') : null;
    if (!grid || !sentinel || !sentinel.dataset.nextUrl ||
        (clickMode && !button) || (!clickMode && !('IntersectionObserver' in window))) return;

    const state = {
      pagination,
      root,
      grid,
      sentinel,
      nextUrl: sentinel.dataset.nextUrl,
      loading: false,
      loadedUrls: new Set(),
      observer: null,
      clickMode,
      button
    };

    if (clickMode) {
      button.addEventListener('click', () => { void loadNext(state); });
    } else {
      state.observer = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) loadNext(state);
      }, { rootMargin: '900px 0px' });
    }

    pagination.dataset.infiniteInitialized = 'true';
    pagination.classList.add('zing-infinite-ready');
    sentinel.classList.add('zing-infinite-ready');
    state.observer?.observe(sentinel);
    states.add(state);
  };

  const initAll = () => {
    cleanupDetached();
    document.querySelectorAll('[data-infinite-pagination]').forEach(initPagination);
  };

  const queueInit = () => {
    window.clearTimeout(mutationTimer);
    mutationTimer = window.setTimeout(initAll, 80);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll, { once: true });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', queueInit);
  document.addEventListener('shapes:section:hasmutated', queueInit);

  const mutationObserver = new MutationObserver((mutations) => {
    if (mutations.some((mutation) => mutation.addedNodes.length || mutation.removedNodes.length)) {
      queueInit();
    }
  });
  mutationObserver.observe(document.documentElement, { childList: true, subtree: true });
})();
